import { Button } from '@heroui/react';
import { ArrowRight, FolderOpen } from 'lucide-react';
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AdPlatform, PLATFORMS, previewRatio } from '../../config/platforms';
import { GENERATOR_ROUTES } from './GeneratorTabs';
import {
  CardHint,
  CardTitle,
  FormatCard,
  FormatGrid,
  FormatPreview,
  FormatPreviewArea,
  PlatformCard,
  PlatformIcon,
  StepDescription,
  StepGrid,
  StepHeader,
  StepHeading,
  StepLabel,
} from './PlatformStep.styled';

interface PlatformStepProps {
  onStart: (platformId: string, formatId: string) => void;
}

/** Krok pierwszy: platforma decyduje o dostępnych formatach i proporcjach planszy. */
export const PlatformStep: FC<PlatformStepProps> = ({ onStart }) => {
  const [platform, setPlatform] = useState<AdPlatform | null>(null);
  const [formatId, setFormatId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handlePlatform = (next: AdPlatform): void => {
    setPlatform(next);
    setFormatId(next.formats[0].id);
  };

  return (
    <div className="flex flex-col gap-8">
      <section>
        <StepHeader>
          <StepLabel>Krok 1</StepLabel>
          <StepHeading>Gdzie publikujesz kreację?</StepHeading>
          <StepDescription>Wybór platformy ustawia listę formatów i bezpieczne marginesy.</StepDescription>
        </StepHeader>
        <StepGrid>
          {PLATFORMS.map((item) => {
            const Icon = item.icon;
            return (
              <PlatformCard
                key={item.id}
                type="button"
                $active={platform?.id === item.id}
                $accent={item.color}
                onClick={() => handlePlatform(item)}
              >
                <PlatformIcon $accent={item.color}>
                  <Icon size={20} />
                </PlatformIcon>
                <span>
                  <CardTitle>{item.name}</CardTitle>
                  <CardHint>{item.description}</CardHint>
                </span>
              </PlatformCard>
            );
          })}
        </StepGrid>
      </section>

      {platform && (
        <section>
          <StepHeader>
            <StepLabel>Krok 2</StepLabel>
            <StepHeading>{`Format dla ${platform.name}`}</StepHeading>
            <StepDescription>Plansza powstanie w docelowej rozdzielczości, więc plik nadaje się do publikacji bez skalowania.</StepDescription>
          </StepHeader>
          <FormatGrid>
            {platform.formats.map((format) => (
              <FormatCard
                key={format.id}
                type="button"
                $active={formatId === format.id}
                onClick={() => setFormatId(format.id)}
              >
                <FormatPreviewArea>
                  <FormatPreview $ratio={previewRatio(format)} $accent={platform.color} />
                </FormatPreviewArea>
                <span>
                  <CardTitle>{format.label}</CardTitle>
                  <CardHint>{`${format.width} x ${format.height} px`}</CardHint>
                  <CardHint>{format.hint}</CardHint>
                </span>
              </FormatCard>
            ))}
          </FormatGrid>

          <div className="mt-6 flex justify-start gap-2">
            <Button
              variant="flat"
              radius="sm"
              startContent={<FolderOpen size={16} />}
              onPress={() => navigate(`${GENERATOR_ROUTES.creatives}?platform=${platform.id}`)}
            >
              {`Gotowe kreacje dla ${platform.name}`}
            </Button>
            <Button
              color="primary"
              radius="sm"
              className="bg-black text-white"
              endContent={<ArrowRight size={16} />}
              isDisabled={!formatId}
              onPress={() => formatId && onStart(platform.id, formatId)}
            >
              Otwórz edytor
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};
